//@ts-ignore
import { defineRouter } from '#q-app/wrappers';
import { AppRouteContext } from "@/core/classes/App";
import Result from "@/core/classes/Result";
import { collectMeta } from "@/core/router/collectMeta";
import { authGuard } from "@/core/router/guards/authGuard";
import { useRouterStore } from "@/core/router/store";
import { useConfig } from "@/core/store/config";
import { QueryScope, useScopeQuery } from "@/core/store/scopeQuery";
import { app } from "@/modules/app";
import { useStackRouter } from "@/packages/stack-router/hooks/useStackRouter";
import { useStackRouterBeforeEach } from "@/packages/stack-router/hooks/useStackRouterGuards";
import { useRootStore } from "@/stores/root";
import {useUtmStore} from "@/modules/marketing/store/utm";

import {
    createMemoryHistory,
    createRouter,
    createWebHashHistory,
    createWebHistory,
    RouteLocationNormalized,
    RouteRecordSingleView,
} from 'vue-router';

import routes from './routes';
import {sleep} from "@/core/util/sleep";

function extractMatchedCurrent(to: RouteLocationNormalized) {
    return to.matched[to.matched.length - 1] as unknown as RouteRecordSingleView
}

function isNotFoundRoute(to: RouteLocationNormalized) {
    return to.matched.some((record) => record.meta?.ssrStatus === 404)
}


export default defineRouter(function (ctx: any) {

    let firstBeforeEach = true
    let firstAfterEach = true

    app.onBoot()

    // initAdvancedPushState()

    const routerStore = useRouterStore()

    if (!process.env.SERVER) {
        window.history.scrollRestoration = 'manual'
    }

    const createHistory = process.env.SERVER ? createMemoryHistory :  createWebHistory

    const router = createRouter({
        scrollBehavior: (to, from) => {
            if (to.hash || to.fullPath === from.fullPath)
                return false
            return {left: 0, top: 0}
        },
        routes: routes as any,
        history: createHistory(process.env.VUE_ROUTER_BASE),
    })

    router.beforeEach(async (to, from, next) => {

        // Debug: логируем все переходы на сервере
        if (process.env.SERVER) {
            console.log('[SSR Router] beforeEach:', {
                to: to.path,
                from: from.path,
            })
        }

        const toCurrent = extractMatchedCurrent(to)
        const fromCurrent = extractMatchedCurrent(from)

        if (toCurrent === fromCurrent) {
            const [toUrl] = to.fullPath.split('#')
            const [fromUrl] = from.fullPath.split('#')
            if (toUrl === fromUrl) {
                // next()
                // return
            }
        }

        const result = new Result()
        const meta = collectMeta(to)
        const context: AppRouteContext = {meta}

        //console.log('context', context)

        const {loadServerConfig} = useConfig()
        const {fetchScopesByRoute} = useScopeQuery()
        const {setRouteMetas} = useRouterStore()

        if (firstBeforeEach) {
            firstBeforeEach = false
            const {storesBoot} = useRootStore()
            await storesBoot()
        }

        if ((await app.runGuardsAsync('routerBeforeEach', to, from, next, context) === false)) {
            if (process.env.SERVER) {
                console.log('[SSR Router] Guard returned false')
            }
            return
        }

        const scopes: QueryScope[] = ['app']

        await fetchScopesByRoute(to, scopes)

        if (process.env.SERVER) {
            const matched = extractMatchedCurrent(to)
            const matchedComponent = matched?.component || (matched?.components as any)?.default
            if (matchedComponent && typeof matchedComponent === 'object' && '_isStackable' in matchedComponent) {
                await matchedComponent.initialize(to)
            }
        }

        if (!process.env.SERVER) {
            useUtmStore().captureFromUrl(to.query, to.fullPath)

            if (typeof window !== 'undefined' && window.hidePreloader) { // ✅ Исправлена проверка
                window.hidePreloader()
            }

            if (meta.auth) {
                if ((await authGuard(to, from, next, meta)) === true) {
                    // authGuard обработал навигацию (запретил доступ)
                    return;
                }
            }

            if ((await useStackRouterBeforeEach(to, from, next)) === true) {
                // useStackRouterBeforeEach обработал навигацию (stack route)
                return;
            }
        }

        setRouteMetas(meta || {})

        next()
    })

    router.afterEach(async (to, from, failure) => {

        if (process.env.SERVER && ctx.ssrContext?.res && isNotFoundRoute(to)) {
            ctx.ssrContext.res.status(404)
        }

        if (process.env.SERVER) {
            console.log('[SSR Router] afterEach:', {
                to: to.path,
                from: from.path,
                failure: failure
            })
        }

        // Временно закомментировано для отладки
        if (process.env.SERVER) {
            console.log('[SSR Router] Skipping runCallbacksAsync on server')
        } else {
            if (firstAfterEach) {
                const scopeQuery = useScopeQuery()
                await scopeQuery.fetchUserScopes()
            }
            await app.runCallbacksAsync('routerAfterEach', to, from, failure)
        }

        firstAfterEach = false

        if (process.env.SERVER) {
            console.log('[SSR Router] afterEach completed')
        }
    })

    if (!process.env.SERVER) {
        const stackRouter = useStackRouter()
        stackRouter.initStack('modal', {
            //backComponent: StackRouteSafe,
            backRedirect: '/',
            routable: true,
            breakpoint: 'md'
        }, router)
    }

    // Инициализируем router store для обработки keep-alive
    routerStore.initRouter(router)

    return router
})
